import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, DollarSign, User, Star, MessageSquare, 
  CheckCircle, RefreshCw, Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

const TipWorker = ({ booking, onClose, onSuccess }) => {
  const [tipAmount, setTipAmount] = useState('');
  const [tipNote, setTipNote] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [customAmount, setCustomAmount] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/tips/settings');
      setSettings(res.data);
    } catch (error) {
      // Use defaults
      setSettings({
        tips_enabled: true,
        min_tip_amount: 1,
        max_tip_amount: 500,
        suggested_percentages: [15, 18, 20, 25]
      });
    }
  };

  const serviceTotal = booking?.total_amount || booking?.total_price || 0;

  const calculateTipFromPercent = (percent) => {
    return ((serviceTotal * percent) / 100).toFixed(2);
  };

  const handleSelectPercentage = (percent) => {
    setTipAmount(calculateTipFromPercent(percent));
    setCustomAmount(false);
  };

  const handleSubmit = async () => {
    const amount = parseFloat(tipAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid tip amount');
      return;
    }

    if (settings?.min_tip_amount && amount < settings.min_tip_amount) {
      toast.error(`Minimum tip is $${settings.min_tip_amount}`);
      return;
    }

    if (settings?.max_tip_amount && amount > settings.max_tip_amount) {
      toast.error(`Maximum tip is $${settings.max_tip_amount}`);
      return;
    }

    setLoading(true);
    try {
      await api.post('/tips', {
        booking_id: booking.id,
        worker_id: booking.worker_id,
        amount: amount,
        note: tipNote || null,
        is_anonymous: isAnonymous
      });

      toast.success(
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-pink-500 fill-pink-500" />
          <span>Thank you for your generosity!</span>
        </div>
      );
      
      onSuccess?.();
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit tip');
    } finally {
      setLoading(false);
    }
  };

  if (!settings?.tips_enabled) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card p-6 max-w-md w-full text-center"
        >
          <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Tipping Unavailable</h2>
          <p className="text-[var(--text-muted)] mb-4">Tipping is currently not available.</p>
          <Button onClick={onClose}>Close</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="card p-6 max-w-md w-full"
        data-testid="tip-worker-modal"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Heart className="w-8 h-8 text-pink-500" />
          </div>
          <h2 className="text-xl font-bold">Show Your Appreciation</h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Leave a tip for {booking?.worker_name}
          </p>
        </div>

        {/* Worker Info */}
        <div className="bg-[var(--accent)] p-4 rounded-xl mb-6 flex items-center gap-4">
          <div className="w-12 h-12 bg-[var(--primary)] rounded-full flex items-center justify-center text-white font-bold">
            {booking?.worker_name?.charAt(0) || 'W'}
          </div>
          <div>
            <p className="font-bold">{booking?.worker_name}</p>
            <p className="text-sm text-[var(--text-muted)]">{booking?.service_name}</p>
          </div>
        </div>

        {/* Service Total Reference */}
        {serviceTotal > 0 && (
          <p className="text-sm text-center text-[var(--text-muted)] mb-4">
            Service total: ${serviceTotal.toFixed(2)}
          </p>
        )}

        {/* Percentage Options */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {(settings?.suggested_percentages || [15, 18, 20, 25]).map(percent => {
            const tipValue = calculateTipFromPercent(percent);
            const isSelected = tipAmount === tipValue && !customAmount;
            return (
              <Button
                key={percent}
                variant={isSelected ? 'default' : 'outline'}
                className={`flex flex-col py-3 h-auto ${isSelected ? 'ring-2 ring-pink-300' : ''}`}
                onClick={() => handleSelectPercentage(percent)}
                data-testid={`tip-${percent}pct`}
              >
                <span className="font-bold">{percent}%</span>
                <span className="text-xs opacity-70">${tipValue}</span>
              </Button>
            );
          })}
        </div>

        {/* Custom Amount */}
        <div className="mb-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { setCustomAmount(true); setTipAmount(''); }}
            className="text-[var(--primary)] mb-2"
          >
            <Sparkles className="w-4 h-4 mr-1" /> Enter custom amount
          </Button>
          
          {customAmount && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
            >
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
                <Input
                  type="number"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min={settings?.min_tip_amount || 1}
                  max={settings?.max_tip_amount || 500}
                  className="pl-10 text-lg"
                  autoFocus
                  data-testid="custom-tip-input"
                />
              </div>
            </motion.div>
          )}
        </div>

        {/* Selected Amount Display */}
        {tipAmount && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl mb-4"
          >
            <p className="text-sm text-[var(--text-muted)]">You're tipping</p>
            <p className="text-4xl font-bold text-pink-600">${parseFloat(tipAmount).toFixed(2)}</p>
          </motion.div>
        )}

        {/* Note */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 flex items-center gap-1">
            <MessageSquare className="w-4 h-4" />
            Add a note <span className="text-[var(--text-muted)]">(optional)</span>
          </label>
          <textarea
            value={tipNote}
            onChange={(e) => setTipNote(e.target.value)}
            placeholder="Thank you for the amazing service!"
            className="w-full p-3 border rounded-xl min-h-[80px] resize-none"
            maxLength={500}
            data-testid="tip-note-input"
          />
        </div>

        {/* Anonymous Option */}
        <label className="flex items-center gap-2 mb-6 cursor-pointer">
          <input 
            type="checkbox" 
            checked={isAnonymous}
            onChange={(e) => setIsAnonymous(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <span className="text-sm">Send anonymously</span>
        </label>

        {/* Actions */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Maybe Later
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !tipAmount || parseFloat(tipAmount) <= 0}
            className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
            data-testid="submit-tip-btn"
          >
            {loading ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
            ) : (
              <><Heart className="w-4 h-4 mr-2" /> Send Tip</>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default TipWorker;
